/**
 * Zod at the boundary over the ops route table.
 *
 * Every ops response is parsed here before a component ever sees it. These
 * payloads are nested numeric objects in two units — tenant credits (price)
 * and provider credits (cost), §18.1 — and a renamed or reordered field would
 * otherwise surface as a blank cell or, worse, as a plausible figure in the
 * wrong column. `z.object` rejects the mismatch outright instead.
 *
 * The backend does not exist yet; every schema below is checked against the
 * documented route table. Several go further and are marked ASSUMED where
 * the route table names a route's purpose but not the field-level shape
 * this plane needs.
 */

import { z } from 'zod';

/**
 * A register row. `available_credits` may be negative: an overrun drives
 * the balance below zero, and the next dispatch is refused because of it —
 * no `.nonnegative()` here, or the one row an operator most needs to see
 * would fail to parse.
 */
export const opsTenantRowSchema = z.object({
  tenant_id: z.string(),
  name: z.string(),
  created_at: z.string(),
  // Null under `PLATFORM_ADMIN`: RLS widens only the commercial tables, so a
  // count over `memberships` for another tenant reads zero rows, not the
  // true count. Null says "not counted", never 0.
  members: z.number().int().nullable(),
  balance_credits: z.number().int(),
  available_credits: z.number().int(), // may be negative
  // The tenant's own credits granted and spent in the trailing 30 days (the
  // same window `/ops/usage` defaults to). Never null: a tenant with no
  // movement in the window reads 0.
  granted_30d: z.number().int(),
  spent_30d: z.number().int(),
  // Distinct accounts the tenant billed (a DEBIT movement) in the trailing 30
  // days. Replaces a raw tracked-account count, which RLS containment made
  // unreadable from this plane; never null.
  billed_accounts_30d: z.number().int(),
  last_movement_at: z.string().nullable(),
  open_credit_request: z.boolean(),
  // Null for the same RLS-containment reason as `members` above, never
  // "unpriced" (platform design §18.4): this is the tenant's funded-window
  // rate, fenced behind the tenant side, not a catalogue price an operator
  // could be missing.
  tenant_credits_per_day: z.number().int().nullable(),
  // Whether the rate above was measured from the ledger or modelled from the
  // configuration. Parsed for parity with the producer's row, but nothing
  // renders it today: it is fenced null under `PlatformAdminDep` the same as
  // the rate it describes, so there is no value here yet for a screen to show.
  burn_source: z.enum(['observed', 'estimated']).nullable(),
  funded_until: z.string().nullable(), // null = unknown, NOT never
});
// §18.3: nothing is purged, so nothing needs a dormancy clock. `z.object`
// strips what it does not name, so a stray `dormant_days` on the wire costs
// this plane nothing and no screen here can accidentally render it.

export type OpsTenantRow = z.infer<typeof opsTenantRowSchema>;

/**
 * The tenant detail screen, matching `OpsTenantDetailOut`
 * (`api/src/socialsrc_api/schemas/ops.py`) field for field — confirmed, not
 * ASSUMED. `tenant_credits_per_day` is parsed by the same nullable rule as
 * the register row's own field on purpose — a reach computed from this
 * screen and one computed from a register row must be the same quotient of
 * the same two integers, which only holds if both come from fields with
 * identical null-handling. `tracked_accounts` stays fenced null under RLS
 * containment the same way, though the register row no longer carries this
 * field at all (`billed_accounts_30d` replaced it there). `held_credits` is
 * added because the balance band names three figures — "balance, held,
 * available" — and the register row carries only two of them.
 */
export const opsTenantDetailSchema = z.object({
  tenant_id: z.string(),
  name: z.string(),
  created_at: z.string(),
  balance_credits: z.number().int(),
  held_credits: z.number().int(),
  available_credits: z.number().int(), // may be negative
  tracked_accounts: z.number().int().nullable(), // null under RLS containment
  // Null for the same RLS-containment reason, never "unpriced" (platform
  // design §18.4).
  tenant_credits_per_day: z.number().int().nullable(),
  burn_source: z.enum(['observed', 'estimated']).nullable(),
  funded_until: z.string().nullable(),
  // `None` for a tenant with no capacity row, never `0` (platform design
  // §5.1.1): distinct from every other null field on this schema, which is
  // an RLS fence, not "not set up yet".
  campaign_slots: z.number().int().nullable(),
  // Never null: a tenant's campaign count is real whether or not it has a
  // capacity row (`api` `OpsTenantDetailOut.campaigns_in_use`).
  campaigns_in_use: z.number().int(),
});

export type OpsTenantDetail = z.infer<typeof opsTenantDetailSchema>;

export const opsMovementSchema = z.object({
  id: z.string(),
  occurred_at: z.string(),
  kind: z.enum(['GRANT', 'DEBIT', 'DEBIT_OVERRUN', 'REFUND', 'ADJUSTMENT', 'HOLD_EXPIRY']),
  credits: z.number().int(), // signed
  provider_credits: z.number().int().nullable(),
  // `email` is null for an actor whose address was never verified
  // (`/control/me` stores `NULL`, not a placeholder) — never filtered out.
  actor: z.object({ user_id: z.string(), email: z.string().nullable() }).nullable(),
  campaign_id: z.string().nullable(),
  note: z.string().nullable(),
  // What a settled debit paid for. `job_type` is a plain string, not an
  // enum: the core job-type vocabulary is wider than the ALVO column has a
  // label for, and a job type this plane does not recognise must still
  // parse -- see `MovementLedger`'s raw-key fallback. `handle` carries no
  // leading `@`, matching the producer's own `OpsMovementTargetOut`
  // docstring. Null for a movement with nothing to point at (a manual
  // grant, most non-debit kinds) — never omitted, or a movement the
  // producer sends with no target at all would fail to parse.
  target: z.object({ job_type: z.string(), handle: z.string().nullable() }).nullable(),
});

export type OpsMovement = z.infer<typeof opsMovementSchema>;

/**
 * `GET /ops/operations`: what each operation runs on and what an
 * organisation pays for it (platform design §18.1, `api`'s
 * `schemas/ops_operations.py`). Spelled from `OperationProvider`
 * (`core` `db/models/operation_configs.py`); `platform` is the one member
 * with no key, since it calls nobody.
 */
export const operationProviderSchema = z.enum([
  'socialcrawl',
  'openrouter',
  'openai',
  'google_ai',
  'anthropic',
  'platform',
]);

export type OperationProvider = z.infer<typeof operationProviderSchema>;

export const keyRefSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type KeyRef = z.infer<typeof keyRefSchema>;

/** Basis and multiplier figures travel the wire as JSON strings: each is an
 *  unbounded `NUMERIC` on the server, and a float would round one. */
const unitBasisSchema = z.object({
  kind: z.literal('unit'),
  credits_per_unit: z.string(),
});

const tokenBasisSchema = z.object({
  kind: z.literal('tokens'),
  credits_per_million_input: z.string(),
  credits_per_million_output: z.string(),
});

export const operationBasisSchema = z.discriminatedUnion('kind', [
  unitBasisSchema,
  tokenBasisSchema,
]);

export type OperationBasis = z.infer<typeof operationBasisSchema>;

export const operationConfigSchema = z.object({
  provider: operationProviderSchema,
  key: keyRefSchema.nullable(), // null only for `platform`, which calls nobody
  basis: operationBasisSchema,
  multiplier: z.string(),
  effective_from: z.string(),
});

export type OperationConfig = z.infer<typeof operationConfigSchema>;

export const operationCatalogueRowSchema = z.object({
  operation_key: z.string(),
  providers: z.array(operationProviderSchema), // the providers this key may run on
  config: operationConfigSchema.nullable(), // null = never saved, unpriced
});

export type OperationCatalogueRow = z.infer<typeof operationCatalogueRowSchema>;

/** `GET /ops/keys`: a key as something that names it shows it -- never its
 *  parameter name or its fingerprint (`api`'s `KeyOut`). A key has no
 *  provider of its own; what names it is every operation and inference
 *  pairing naming it, already summed server-side into `used_by`. */
export const opsKeySchema = z.object({
  id: z.string(),
  name: z.string(),
  /** The provider the key authenticates to; `null` on a key registered before keys named one. */
  provider: operationProviderSchema.nullable(),
  state: z.enum(['ACTIVE', 'RETIRED']),
  last_four: z.string(),
  created_at: z.string(),
  last_changed_at: z.string(),
  retired_at: z.string().nullable(),
  used_by: z.array(z.string()),
});

export type OpsKey = z.infer<typeof opsKeySchema>;

/** `GET /ops/inference`: one `task_inference_configs` row per `TaskType`
 *  (`api`'s `InferenceTaskOut`). `config` is a JSON object on the wire
 *  (`InferenceTaskOut.config: dict[str, object]`) -- only the *save*,
 *  `PUT /ops/inference/{task}`, takes it as operator-typed JSON text. */
export const inferenceTaskSchema = z.object({
  task: z.string(),
  provider: z.string(),
  config: z.record(z.string(), z.unknown()),
  key: keyRefSchema.nullable(),
  secondary_provider: z.string().nullable(),
  secondary_model_id: z.string().nullable(),
  secondary_key: keyRefSchema.nullable(),
  priority: z.enum(['default', 'low']),
  flex_fallback_after: z.number().int().positive().nullable(),
});

export type InferenceTask = z.infer<typeof inferenceTaskSchema>;

export const tenantCreditEstimateSchema = z.object({
  nodes: z.array(
    z.object({
      path: z.array(z.string()),
      provider_credits_per_day: z.number(),
      tenant_credits_per_day: z.number().int().nullable(), // null IS the unpriced signal
      operation_key: z.string().nullable(),
    }),
  ),
  tenant_credits_per_day: z.number().int().nullable(),
  tenant_credits_per_30d: z.number().int().nullable(),
  provider_credits_per_day: z.number(),
  unpriced_operations: z.array(z.string()),
  price_list_at: z.string(),
});

export type TenantCreditEstimate = z.infer<typeof tenantCreditEstimateSchema>;
export type EstimateNode = TenantCreditEstimate['nodes'][number];

export const creditRequestSchema = z.object({
  id: z.string(),
  kind: z.enum(['CREDITS', 'SLOTS']),
  tenant_id: z.string(),
  tenant_name: z.string(),
  requested_by: z.string(),
  accounts_to_track: z.number().int().nullable(),
  credits_requested: z.number().int().nullable(),
  slots_requested: z.number().int().nullable(),
  context: z.string(),
  state: z.enum(['OPEN', 'IN_REVIEW', 'GRANTED', 'DECLINED', 'WITHDRAWN']),
  created_at: z.string(),
  decided_by: z.string().nullable(),
  decided_at: z.string().nullable(),
  decision_note: z.string().nullable(),
  granted_movement_id: z.string().nullable(),
  granted_slot_movement_id: z.string().nullable(),
});

export type CreditRequest = z.infer<typeof creditRequestSchema>;

export const createTenantInputSchema = z.object({
  name: z.string(),
  owner_email: z.string(),
  initial_credits: z.number().int(),
  note: z.string().nullable(),
});

export type CreateTenantInput = z.infer<typeof createTenantInputSchema>;

export const opsTenantCreatedSchema = z.object({
  tenant_id: z.string(),
  name: z.string(),
  owner_user_id: z.string(),
  balance_credits: z.number().int(),
});

export type OpsTenantCreated = z.infer<typeof opsTenantCreatedSchema>;

export const operatorSpendRowSchema = z.object({
  day: z.string(),
  tenant_id: z.string(),
  tenant_name: z.string(),
  tenant_credits: z.number().int(),
  provider_credits: z.number().int(),
  // Null under `PLATFORM_ADMIN`: `schedule_ledger` stays fenced under the
  // same RLS widening that leaves `members` null above, so the token sums
  // are unknown for the row, never a true zero.
  input_tokens: z.number().int().nullable(),
  output_tokens: z.number().int().nullable(),
  calls: z.number().int(),
});

export type OperatorSpendRow = z.infer<typeof operatorSpendRowSchema>;

/**
 * A row on the platform administrator roster, matching `PlatformAdminOut`
 * (`api/src/socialsrc_api/schemas/ops.py`) field for field -- confirmed, not
 * ASSUMED. `GET /ops/admins` and `POST /ops/admins` both return this shape
 * (`api/src/socialsrc_api/routers/ops_admin.py`).
 */
export const platformAdminSchema = z.object({
  user_id: z.string(),
  // Null for an administrator whose address is not verified yet — never
  // filtered out, since that would hide a real grant from the roster.
  email: z.string().nullable(),
  granted_at: z.string(),
});

export type PlatformAdmin = z.infer<typeof platformAdminSchema>;

/**
 * `GET /ops/usage/platform`, matching `PlatformMarginOut` field for field:
 * the two figures are independently nullable and land `null` together for a
 * period with neither spend nor cost, never `0`.
 */
export const platformMarginSchema = z.object({
  provider_credits: z.number().int().nullable(),
  margin_credits: z.number().int().nullable(),
});

export type PlatformMargin = z.infer<typeof platformMarginSchema>;

/** `GET /ops/privacy/thresholds`, matching `ThresholdOut`. `key` is one of
 *  the two k thresholds; `enabled: false` means no cell is withheld. */
export const privacyThresholdSchema = z.object({
  key: z.enum(['k_general', 'k_sensitive']),
  value: z.number().int().min(1),
  enabled: z.boolean(),
});

export type PrivacyThreshold = z.infer<typeof privacyThresholdSchema>;

/** `GET /ops/collection/post-stats`, matching `PostStatsSettingsOut`. */
export const postStatsSettingsSchema = z.object({
  enabled: z.boolean(),
  supporter_factor: z.number().int().min(1),
  supporter_cap_seconds: z.number().int().min(1),
  min_interval_seconds: z.number().int().min(1),
  likers_max_per_post: z.number().int().min(1).max(5000),
});

export type PostStatsSettings = z.infer<typeof postStatsSettingsSchema>;
